import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import { styled } from '@mui/styles';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Collapse,
  Grow,
  Slide,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from '@mui/material';
import CustomLabel from 'ui-components/CustomLabel';
import CustomInputField from 'ui-components/CustomInputField';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  PictureAsPdf as PdfIcon,
  AutoFixHigh as SparkleIcon,
  FileUpload as UploadIcon,
  AutoAwesomeOutlined as ShortlistedIcon,
  ThumbDownOutlined as RejectedIcon,
  ErrorOutlineRounded as FailedIcon,
  FileCopyOutlined as DuplicateIcon,
  TaskAltOutlined as SuccessIcon,
  NotificationsOutlined as NotifyIcon,
} from '@mui/icons-material';
import ModalComponent from 'ui-components/ModalComponent';
import { useToast } from 'context/ToastContext';
import { useProcessingSnackbar } from 'context/ProcessingSnackbarContext';
import { UploadPitchDeckBatch, FetchBatchStatus, FetchPipelinesStatuses, NotifyBatchStartups } from 'api/startup_applications';
import { FetchAllBusinessModel, FetchAllCategories, FetchAllStages, FetchAllSubCategories } from 'api/utils';
import { FetchDealFlowConfig, GetInvestorSetting } from 'api/investors';
import { categoryIdsToNames } from 'utils';
import PerfectScrollbar from 'react-perfect-scrollbar';
import AssessmentFooter from '../../tools/components/AssessmentFooter';
import { Primary } from 'ui-components/Buttons';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const STORAGE_KEY = 'pitchdeck_import_state';

const PitchDeckImportPopup = ({ open, handleClose, investor_id, refetchListing, onProcessingChange }) => {
  const theme = useTheme();
  const { addToast } = useToast();
  const { addBatch } = useProcessingSnackbar();
  const c = theme.palette.customColors;

  // ── restore persisted state on mount ──────────────────────────────────
  const getInitialState = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved?.batchId && saved?.investorId === investor_id && saved?.step === 'processing') {
        return { step: 'processing', batchId: saved.batchId, uploadedFileNames: saved.uploadedFileNames || [] };
      }
    } catch (_) { }
    return { step: 'select', batchId: null, uploadedFileNames: [] };
  };

  const initial = getInitialState();

  // ── state ─────────────────────────────────────────────────────────────
  const [step, setStep] = useState(initial.step);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [batchId, setBatchId] = useState(initial.batchId);
  const [uploadedFileNames, setUploadedFileNames] = useState(initial.uploadedFileNames);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [countdown, setCountdown] = useState(initial.step === 'processing' ? 0 : 5);
  const [snackbarCollapsed, setSnackbarCollapsed] = useState(false);
  const [notifyOnComplete, setNotifyOnComplete] = useState(
    () => JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')?.notifyOnComplete ?? false
  );
  const notifiedRef = useRef(false);
  const countdownRef = useRef(null);

  const [shortlistConfig, setShortlistConfig] = useState({
    category: '',
    sector: '',
    stage: '',
    business_model: '',
    startup_criteria: '',
    selected_status_id: '',
    rejected_status_id: '',
  });
  const [pipelineStatuses, setPipelineStatuses] = useState([]);
  const [shortlistErrors, setShortlistErrors] = useState({});

  // ── API hooks ─────────────────────────────────────────────────────────
  const { data: dealFlowConfig } = FetchDealFlowConfig();
  const { data: investorSetting } = GetInvestorSetting({ investor_id, module: 'deal_flow' });
  const { mutate: uploadBatch, isLoading: isUploading } = UploadPitchDeckBatch({});
  // const { mutate: notifyBatch } = NotifyBatchStartups();
  const { data: batchStatus } = FetchBatchStatus({ batch_id: batchId, enabled: step === 'processing' });
  const { mutateAsync: fetchPipelineStatuses } = FetchPipelinesStatuses();
  const { data: categories = [] } = FetchAllCategories();
  const { data: stages = [] } = FetchAllStages();
  const { data: subCategories = [] } = FetchAllSubCategories({ category: shortlistConfig?.selectedCategories?.split(',') || '' });
  const { data: businessModels = [] } = FetchAllBusinessModel();

  // ── effects ───────────────────────────────────────────────────────────

  // Persist processing state to localStorage so refresh restores the snackbar
  useEffect(() => {
    if (step === 'processing' && batchId) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, batchId, uploadedFileNames, investorId: investor_id, notifyOnComplete }));
      onProcessingChange?.(true);
    }
  }, [step, batchId, notifyOnComplete]);

  // Clear storage and notify parent when done or dismissed
  const clearPersistedState = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  // Pre-fill shortlistConfig from saved Investment Thesis when popup opens.
  // Waits for `categories` — selectedCategories (names) drives the sector fetch and has to
  // be derived from the saved category ids when the thesis didn't store it.
  useEffect(() => {
    if (!open) return;
    const thesis = investorSetting?.investment_thesis;
    if (!thesis || !categories?.length) return;
    setShortlistConfig((prev) => ({
      ...prev,
      selected_status_id: thesis.selected_status_id || prev.selected_status_id,
      rejected_status_id: thesis.rejected_status_id || prev.rejected_status_id,
      stage: thesis.stage || prev.stage,
      category: thesis.category || prev.category,
      selectedCategories: thesis.selectedCategories
        || categoryIdsToNames(thesis.category, categories)
        || prev.selectedCategories,
      sector: thesis.sector || prev.sector,
      business_model: thesis.business_model || prev.business_model,
      startup_criteria: thesis.startup_criteria || prev.startup_criteria,
    }));
  }, [open, investorSetting, categories]);

  useEffect(() => {
    if (aiEnabled && investor_id && pipelineStatuses.length === 0) {
      fetchPipelineStatuses({ investor_id }, {
        onSuccess: (res) => {
          setPipelineStatuses(
            res?.data?.data?.map((item) => ({ label: item.status, value: item._id, mappedValue: item.value })) || []
          );
        },
      });
    }
  }, [aiEnabled]);

  // Block page unload while uploading
  useEffect(() => {
    if (!isUploading) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  useEffect(() => {
    if (step === 'processing' && countdown > 0) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
          return prev - 1;
        });
      }, 5000);
    }
    return () => clearInterval(countdownRef.current);
  }, [step]);

  useEffect(() => {
    if (batchStatus && ['completed', 'failed', 'partially_completed'].includes(batchStatus.status)) {
      setStep('complete');
      clearPersistedState();
      onProcessingChange?.(false);
      refetchListing?.();

      // Fire browser notification if user opted in and we haven't notified yet this session
      if (notifyOnComplete && !notifiedRef.current && Notification.permission === 'granted') {
        notifiedRef.current = true;
        const total = batchStatus.total_files ?? 0;
        const success = (batchStatus.success_count ?? 0) + (batchStatus.shortlisted_count ?? 0);
        new Notification('Pitch Deck Import Complete', {
          body: `${total} file${total !== 1 ? 's' : ''} processed - ${success} startup${success !== 1 ? 's' : ''} imported.`,
          icon: '/favicon.ico',
        });
      }
    }
  }, [batchStatus?.status]);

  // ── handlers ──────────────────────────────────────────────────────────
  const handleFileSelect = (files) => {
    const newFiles = Array.from(files);
    const errors = [];
    newFiles.forEach((file) => {
      if (file.type !== 'application/pdf') errors.push(`"${file.name}" is not a PDF`);
      else if (file.size > 50 * 1024 * 1024) errors.push(`"${file.name}" exceeds the 50MB limit`);
    });
    let validNew = newFiles.filter((f) => f.type === 'application/pdf' && f.size <= 50 * 1024 * 1024);
    const remaining = 50 - selectedFiles.length;
    if (validNew.length > remaining) {
      errors.push(`Only ${remaining} more file${remaining !== 1 ? 's' : ''} can be added (max 50). ${validNew.length - remaining} file${validNew.length - remaining !== 1 ? 's' : ''} skipped.`);
      validNew = validNew.slice(0, remaining);
    }
    errors.forEach((e) => addToast(e, 'error'));
    if (validNew.length > 0) setSelectedFiles((prev) => [...prev, ...validNew]);
  };

  const resetModal = () => {
    setStep('select');
    setSelectedFiles([]);
    setBatchId(null);
    setUploadedFileNames([]);
    setAiEnabled(false);
    setShortlistConfig({ category: '', selectedCategories: '', sector: '', stage: '', business_model: '', startup_criteria: '', selected_status_id: '', rejected_status_id: '' });
    setPipelineStatuses([]);
  };

  const handleCloseModal = () => {
    if (isUploading) {
      addToast('Closing will lose unfinished uploads. Please wait for the upload to complete.', 'warning');
      return;
    }
    handleClose();
    // only reset if not processing — snackbar stays visible
    if (step !== 'processing' && step !== 'complete') resetModal();
  };

  const handleDismissSnackbar = () => {
    setSnackbarCollapsed(false);
    clearPersistedState();
    onProcessingChange?.(false);
    resetModal();
  };

  useEffect(() => {
    if (open) {
      handleDismissSnackbar();
    }
  }, [open])

  const handleUpload = () => {
    if (aiEnabled) {
      const errors = {};
      if (!shortlistConfig.selected_status_id) errors.selected_status_id = 'Required';
      if (!shortlistConfig.rejected_status_id) errors.rejected_status_id = 'Required';
      if (!shortlistConfig.stage) errors.stage = 'Required';
      if (Object.keys(errors).length > 0) {
        setShortlistErrors(errors);
        return;
      }
    }
    setShortlistErrors({});
    const formData = new FormData();
    selectedFiles.forEach((f) => formData.append('files', f));
    formData.append('user_profile_id', investor_id);
    formData.append('ai_shortlist_enabled', String(aiEnabled));
    if (aiEnabled) {
      Object.entries(shortlistConfig).forEach(([k, v]) => formData.append(`shortlist_config[${k}]`, v));
    }
    uploadBatch(formData, {
      onSuccess: (res) => {
        clearPersistedState();
        notifiedRef.current = false;
        const newBatchId = res?.data?.data?.batch_id;
        const fileNames = selectedFiles.map((f) => ({ file_name: f.name, status: 'uploaded' }));
        addBatch(newBatchId, {
          batchType: 'bulk_analysis',
          uploadStep: 'processing',
          uploadedFileNames: fileNames,
          notifyOnComplete: false,
        });
        setSelectedFiles([]);
        onProcessingChange?.(true);
        handleClose();
        refetchListing?.();
      },
      onError: (err) => {
        addToast(err?.response?.data?.message || 'Upload failed. Please try again.', 'error');
      },
    });
  };

  const handleRequestNotify = async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      setNotifyOnComplete(true);
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') setNotifyOnComplete(true);
    }
  };

  const setConfig = (patch) => {
    setShortlistErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
    setShortlistConfig((prev) => ({ ...prev, ...patch }));
  };

  const multiSelectProps = (field, options, placeholder) => ({
    multiple: true,
    disablePortal: true,
    fullWidth: true,
    autoHighlight: true,
    options: options.filter((o) => !shortlistConfig[field].split(',').filter(Boolean).includes(o.value)),
    value: shortlistConfig[field].split(',').filter(Boolean).map((v) => {
      return ({ label: options?.find((item) => item?.value === v)?.label, value: v })
    }),
    onChange: (_, newVal) => setConfig({ [field]: newVal.map((o) => o.value).join(',') }),
    renderTags: (selected, getTagProps) =>
      selected.map((opt, i) => {
        return (
          <Chip key={opt.value} label={opt.label} {...getTagProps({ index: i })} />
        )
      }),
    renderInput: (params) => (
      <CustomInputField
        variant="outlined"
        placeholder={placeholder}
        {...params}
        sx={{ marginBottom: 0 }}
        className="filter-autocomplete"
      />
    ),
  });

  const hasFiles = selectedFiles.length > 0;
  const showConfig = aiEnabled;

  // ── shared sub-components ────────────────────────────────────────────
  const Footer = () => (
     <AssessmentFooter />
  );

  const Header = ({ title, onClose }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: c.g300 }}>
        {title}
      </Typography>
      {onClose && (
        <IconButton onClick={onClose} size="small" sx={{ p: 0.5, color: c.g100, '&:hover': { backgroundColor: c.g50 } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
    </Box>
  );

  // console.log(shortlistConfig, "shortlistConfig");

  // ── STEP: SELECT ─────────────────────────────────────────────────────
  const renderSelect = () => (
    <Box sx={{ p: { xs: '1.25rem', sm: '1.75rem 1.75rem 1.5rem' } }}>
      <Header title={showConfig ? 'Pitch Deck Shortlisting' : 'Pitch Deck Import'} onClose={handleCloseModal} />

      {/* Upload-in-progress warning */}
      {isUploading && (
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1,
          px: 1.5, py: 1.25, mb: 2,
          backgroundColor: '#fffbeb',
          border: '1px solid #FCD34D',
          borderRadius: '10px',
        }}>
          <InfoOutlinedIcon sx={{ fontSize: 17, color: '#D97706', mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.82rem', color: '#92400E', lineHeight: 1.5 }}>
            Do not refresh or close this page while files are uploading. Uploaded files will be processed, but unfinished uploads will be lost.
          </Typography>
        </Box>
      )}

      {/* Form body — locked while uploading */}
      <Box sx={isUploading ? { pointerEvents: 'none', opacity: 0.5, userSelect: 'none' } : {}}>

        {/* AI Shortlist config — expands when AI toggled on */}
        {showConfig && (
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <CustomLabel>Shortlisted Status <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
              <Autocomplete
                fullWidth disablePortal autoHighlight
                options={pipelineStatuses.filter((s) => {
                  const excluded = dealFlowConfig?.deal_flow?.investment_thesis?.excluded_selected_status_ids || [];
                  return s.mappedValue !== shortlistConfig.rejected_status_id && !excluded.includes(s.mappedValue);
                })}
                value={pipelineStatuses.find((s) => s.value === shortlistConfig.selected_status_id) || null}
                onChange={(_, newVal) => setConfig({ selected_status_id: newVal?.value || '' })}
                renderInput={(params) => (
                  <CustomInputField variant="outlined" placeholder="Select status" {...params} sx={{ marginBottom: 0 }} className="filter-autocomplete" />
                )}
              />
              {shortlistErrors.selected_status_id && (
                <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{shortlistErrors.selected_status_id}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomLabel>Rejected Status <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
              <Autocomplete
                fullWidth disablePortal autoHighlight
                options={pipelineStatuses.filter((s) => {
                  const excluded = dealFlowConfig?.deal_flow?.investment_thesis?.excluded_rejected_status_ids || [];
                  return s.mappedValue !== shortlistConfig.selected_status_id && !excluded.includes(s.mappedValue);
                })}
                value={pipelineStatuses.find((s) => s.value === shortlistConfig.rejected_status_id) || null}
                onChange={(_, newVal) => setConfig({ rejected_status_id: newVal?.value || '' })}
                renderInput={(params) => (
                  <CustomInputField variant="outlined" placeholder="Select status" {...params} sx={{ marginBottom: 0 }} className="filter-autocomplete" />
                )}
              />
              {shortlistErrors.rejected_status_id && (
                <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{shortlistErrors.rejected_status_id}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomLabel>Stage <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
              <Autocomplete
                {...multiSelectProps('stage', stages?.map((s) => ({ label: s.name, value: s?._id, _id: s?._id })) || [], 'Stage')}
              />
              {shortlistErrors.stage && (
                <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{shortlistErrors.stage}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomLabel>Category</CustomLabel>
              <Autocomplete
                {...multiSelectProps('category', categories?.map((cat) => ({ label: cat.name, value: cat?._id, _id: cat?._id })) || [], 'Category')}
                onChange={(_, newVal) => setConfig({
                  selectedCategories: newVal.map((o) => o.label).join(','),
                  category: newVal?.map(item => item?.value)?.join(','),
                  sector: '',
                })}
              />
            </Grid>
            {shortlistConfig.category && (
              <Grid item xs={12} sm={6}>
                <CustomLabel>Sector</CustomLabel>
                <Autocomplete
                  {...multiSelectProps('sector', subCategories?.map((s) => ({ label: s.sub_category, value: s?._id, _id: s?._id })) || [], 'Sector')}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={shortlistConfig.category ? 6 : 12}>
              <CustomLabel>Business Model</CustomLabel>
              <Autocomplete
                {...multiSelectProps('business_model', businessModels?.map((s) => ({ label: s.name, value: s?._id, _id: s?._id })) || [], 'Business Model')}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <CustomLabel>Startup Criteria</CustomLabel>
                <Tooltip title="Describe what makes an ideal startup for your portfolio" placement="top" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: c.g100, mb: '8px', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <TextField
                fullWidth multiline rows={3} variant="outlined"
                placeholder="Example: High-growth SaaS with at least 2 co-founders..."
                value={shortlistConfig.startup_criteria}
                onChange={(e) => setConfig({ startup_criteria: e.target.value })}
                sx={{
                  marginBottom: 0,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem' },
                  '& .MuiInputBase-input::placeholder': { color: c.g100, opacity: 1 },
                }}
              />
            </Grid>
          </Grid>
        )}

        {/* Upload / Attached zone */}
        <Box
          component={hasFiles ? 'div' : 'label'}
          onDrop={!hasFiles ? (e) => { e.preventDefault(); setDragOver(false); handleFileSelect(e.dataTransfer.files); } : undefined}
          onDragOver={!hasFiles ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
          onDragLeave={!hasFiles ? () => setDragOver(false) : undefined}
          sx={{
            display: 'block',
            border: hasFiles
              ? `2px solid #16A34A`
              : `2px dashed ${dragOver ? c.b300 : c.b200}`,
            borderRadius: '12px',
            p: { xs: '1.25rem 1rem', sm: '1.75rem 1.5rem' },
            textAlign: 'center',
            cursor: hasFiles ? 'default' : 'pointer',
            backgroundColor: hasFiles ? '#f0fdf4' : dragOver ? c.b50 : c.indigo50,
            mb: 2,
            transition: 'border-color 0.2s ease, background-color 0.2s ease',
          }}
        >
          <Box sx={{
            width: 48, height: 48, borderRadius: '12px',
            backgroundColor: c.whiteBG,
            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            {hasFiles
              ? <CheckCircleIcon sx={{ color: '#16A34A', fontSize: 26 }} />
              : <UploadIcon sx={{ color: c.b300, fontSize: 24 }} />}
          </Box>

          {hasFiles ? (
            <>
              <Typography sx={{ fontWeight: 700, color: '#15803D', fontSize: '0.95rem', mb: 0.5 }}>
                {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''} Ready
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#166534', mb: 1.25, lineHeight: 1.5 }}>
                {showConfig ? 'Ready for AI analysis & shortlisting' : 'Ready for AI parsing'}
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                sx={{
                  borderColor: '#16A34A', color: '#15803D', borderRadius: '8px',
                  fontWeight: 600, fontSize: '0.8rem', textTransform: 'none',
                  '&:hover': { backgroundColor: '#dcfce7', borderColor: '#16A34A' },
                }}
              >
                Change Files
                <VisuallyHiddenInput type="file" accept="application/pdf" multiple onChange={(e) => {
                  setSelectedFiles([]);
                  handleFileSelect(e.target.files);
                }} />
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700, color: c.b300, fontSize: '0.95rem', mb: 0.5 }}>
                Select Pitch Decks
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: c.g200, lineHeight: 1.5 }}>
                Drag & drop or click — PDF files up to 50MB, max 50 files
              </Typography>
              <VisuallyHiddenInput type="file" accept="application/pdf" multiple onChange={(e) => handleFileSelect(e.target.files)} />
            </>
          )}
        </Box>

        {/* AI Shortlist toggle */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 1,
          backgroundColor: aiEnabled ? c.b50 : c.inputField,
          border: `1px solid ${aiEnabled ? c.b100 : c.gray300}`,
          borderRadius: '10px',
          mb: 2,
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          cursor: 'pointer',
        }} onClick={() => setAiEnabled((v) => !v)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={aiEnabled}
              onChange={(e) => { e.stopPropagation(); setAiEnabled(e.target.checked); }}
              sx={{ p: 0, color: c.g75, '&.Mui-checked': { color: c.checkboxcolor }, '& .MuiSvgIcon-root': { fontSize: 20 } }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: aiEnabled ? c.b300 : c.g300, userSelect: 'none' }}>
              AI Shortlisting
            </Typography>
            <SparkleIcon sx={{ fontSize: 16, color: aiEnabled ? c.b200 : c.g75 }} />
          </Box>
          <Tooltip title="Automatically score and shortlist startups based on your criteria" placement="top" arrow>
            <InfoOutlinedIcon sx={{ fontSize: 17, color: c.g100 }} />
          </Tooltip>
        </Box>

      </Box>{/* end form-locked zone */}

      {/* CTA */}
      <Primary
        onClick={hasFiles ? handleUpload : undefined}
        component={hasFiles ? 'button' : 'label'}
        fullWidth
        disabled={isUploading}
        sx={{
          height: '48px', fontSize: '0.95rem', fontWeight: 600, borderRadius: '10px',
          mb: 0.75, color: theme.palette.customColors.whiteBG,
          ':disabled': { color: theme.palette.customColors.whiteBG },
        }}
        startIcon={isUploading ? <CircularProgress size={18} sx={{ color: c.whiteBG }} /> : <SparkleIcon />}
      >
        {isUploading
          ? 'Uploading...'
          : showConfig
            ? hasFiles ? `Analyze ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}` : 'Upload Pitch Decks'
            : hasFiles
              ? `Parse ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
              : 'Upload Pitch Decks'}
        {!hasFiles && (
          <VisuallyHiddenInput type="file" accept="application/pdf" multiple onChange={(e) => handleFileSelect(e.target.files)} />
        )}
      </Primary>

      <Footer />
    </Box>
  );

  const isAiEnabled = batchStatus?.ai_shortlist_enabled ?? aiEnabled;

  // ── SNACKBAR: file row ────────────────────────────────────────────────
  const FILE_STATUS_META = {
    created: { Icon: SuccessIcon, color: '#16A34A', tooltip: null },
    shortlisted: { Icon: ShortlistedIcon, color: '#16A34A', tooltip: null },
    rejected: { Icon: RejectedIcon, color: '#DC2626', tooltip: null },
    failed: { Icon: FailedIcon, color: isAiEnabled ? '#D97706' : '#DC2626', tooltip: null },
    not_pitch_deck: { Icon: FailedIcon, color: '#D97706', tooltip: 'Not a pitch deck' },
    duplicate_skipped: { Icon: DuplicateIcon, color: c.b300, tooltip: 'Duplicate' },
  };

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  const tooltipProps = {
    placement: 'top-start',
    arrow: true,
    ...(isTouchDevice && {
      disableHoverListener: true,
      disableFocusListener: true,
      enterTouchDelay: 0,
      leaveTouchDelay: 3000,
    }),
    componentsProps: {
      tooltip: { sx: { backgroundColor: c.g300, color: c.whiteBG, fontSize: '0.75rem', borderRadius: '6px', px: 1.25, py: 0.75, maxWidth: 260 } },
      arrow: { sx: { color: c.g300 } },
    },
  };

  const renderFileRow = (file, i, arr) => {
    const meta = FILE_STATUS_META[file.status];
    const tooltipText = file.failure_reason || meta?.tooltip || '';
    const statusIcon = meta ? (
      <Tooltip title={tooltipText} disableHoverListener={!tooltipText} {...tooltipProps}>
        <meta.Icon sx={{ fontSize: 18, color: meta.color, flexShrink: 0, ml: 1 }} />
      </Tooltip>
    ) : (
      <CircularProgress size={18} sx={{ color: c.b300, flexShrink: 0, ml: 1 }} />
    );

    return (
      <Box key={i} sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 0.875,
        borderBottom: i < arr.length - 1 ? `1px solid ${c.gray200}` : 'none',
        cursor: tooltipText ? 'help' : 'default',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <PdfIcon sx={{ fontSize: 16, color: c.r300, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.82rem', color: c.g300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.file_name}
          </Typography>
        </Box>
        {statusIcon}
      </Box>
    );
  };

  // ── SNACKBAR card (processing + complete) ────────────────────────────
  const renderSnackbar = () => {
    const files = batchStatus?.files ?? uploadedFileNames;
    const isComplete = step === 'complete';
    const total = (batchStatus?.total_files ?? uploadedFileNames.length);

    // Descriptive title based on outcome
    const snackbarTitle = isComplete
      ? `${total} File${total !== 1 ? 's' : ''} Processed`
      : `Processing ${total} File${total !== 1 ? 's' : ''}...`;

    const successCount = batchStatus?.success_count;
    const failedCount = batchStatus?.failed_count;
    const duplicateCount = batchStatus?.duplicate_count;
    const shortlistedCount = batchStatus?.shortlisted_count;
    const rejectedCount = batchStatus?.rejected_count;
    const isAiEnabled = batchStatus?.ai_shortlist_enabled ?? aiEnabled;

    return (
      <Slide in direction="left" mountOnEnter unmountOnExit>
        <Box sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: '50%', sm: 24 },
          transform: { xs: 'translateX(50%) !important', sm: 'none !important' },
          zIndex: 1500,
          width: { xs: 'calc(100vw - 2rem)', sm: 340 },
          maxWidth: 340,
          backgroundColor: c.whiteBG,
          borderRadius: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          overflow: 'hidden',
        }}>
          {/* Header — clickable to collapse/expand */}
          <Box
            onClick={() => setSnackbarCollapsed((v) => !v)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, pt: 1.5, pb: snackbarCollapsed ? 1.5 : 1,
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'background 0.15s',
              borderBottom: snackbarCollapsed ? "none" : `1px solid ${c.gray200}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isComplete && <CircularProgress size={18} sx={{ color: c.b300, flexShrink: 0 }} />}
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: c.g300 }}>
                {snackbarTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="medium"
                sx={{ p: 0.25, color: c.g100 }}
                onClick={(e) => { e.stopPropagation(); setSnackbarCollapsed((v) => !v); }}
              >
                {snackbarCollapsed
                  ? <ExpandLessIcon sx={{ fontSize: 26 }} />
                  : <ExpandMoreIcon sx={{ fontSize: 26 }} />}
              </IconButton>
              {/* Close only available after processing completes */}
              <Grow in={isComplete} unmountOnExit>
                <IconButton
                  size="medium"
                  sx={{ p: 0.25, color: c.g100 }}
                  onClick={(e) => { e.stopPropagation(); handleDismissSnackbar(); }}
                >
                  <CloseIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Grow>
            </Box>
          </Box>

          <Collapse in={!snackbarCollapsed}>
            {/* Summary stats */}
            {isComplete && (() => {
              const statItems = isAiEnabled
                ? [
                  { label: 'Selected', value: shortlistedCount ?? 0, color: '#16A34A', bg: '#f0fdf4', Icon: ShortlistedIcon },
                  { label: 'Rejected', value: rejectedCount ?? 0, color: '#DC2626', bg: '#fef2f2', Icon: RejectedIcon },
                  { label: 'Failed', value: failedCount ?? 0, color: '#D97706', bg: '#fffbeb', Icon: FailedIcon },
                  { label: 'Duplicate', value: duplicateCount ?? 0, color: c.b300, bg: c.indigo50, Icon: DuplicateIcon },
                ]
                : [
                  { label: 'Success', value: successCount ?? 0, color: '#16A34A', bg: '#f0fdf4', Icon: SuccessIcon },
                  { label: 'Failed', value: failedCount ?? 0, color: '#DC2626', bg: '#fef2f2', Icon: FailedIcon },
                  { label: 'Duplicate', value: duplicateCount ?? 0, color: c.b300, bg: c.indigo50, Icon: DuplicateIcon },
                ];

              return (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min(statItems.length, 4)}, 1fr)`,
                  gap: 0.5,
                  px: 2, py: 1,
                  borderBottom: `1px solid ${c.gray200}`,
                }}>
                  {statItems.map((stat) => (
                    <Box key={stat.label} sx={{
                      textAlign: 'center', borderRadius: '8px',
                      backgroundColor: stat.bg, py: 0.75, px: 0.5,
                    }}>
                      <stat.Icon sx={{ fontSize: 16, color: stat.color, mb: 0.25 }} />
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: stat.color, fontWeight: 600, opacity: 0.8, mt: 0.25, lineHeight: 1 }}>{stat.label}</Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}

            {/* AI countdown banner */}
            {/* {!isComplete && countdown > 0 && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 0.875,
                backgroundColor: c.b50,
                borderTop: `1px solid ${c.b100}`,
                borderBottom: `1px solid ${c.b100}`,
              }}>
                <SparkleIcon sx={{ fontSize: 15, color: c.b300 }} />
                <Typography sx={{ fontSize: '0.82rem', color: c.b300, fontWeight: 500 }}>
                  AI analysis starting in {countdown}s…
                </Typography>
              </Box>
            )} */}

            {/* File list */}
            <Box sx={{ px: 2, py: 1, maxHeight: 220, overflowY: 'auto' }}>
              {files.map((file, i, arr) => renderFileRow(file, i, arr))}
            </Box>

            {/* Bottom action row — consistent single-row pattern */}
            {!isComplete && <Box sx={{ borderTop: `1px solid ${c.gray200}`, px: 2, py: 1 }}>
              {/* {isComplete ? (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<NotifyIcon sx={{ fontSize: 15 }} />}
                  onClick={() => notifyBatch({ batch_id: batchId }, {
                    onSuccess: () => addToast('Startups notified successfully', 'success'),
                    onError: () => addToast('Failed to notify startups', 'error'),
                  })}
                  sx={{
                    borderRadius: '8px', fontWeight: 600, fontSize: '0.82rem',
                    textTransform: 'none', borderColor: c.b200, color: c.b300,
                    '&:hover': { backgroundColor: c.b50, borderColor: c.b300 },
                  }}
                >
                  Notify Imported Startups
                </Button>
              ) : */}
              {!isComplete && (notifyOnComplete ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <NotifyIcon sx={{ fontSize: 15, color: c.g100 }} />
                  <Typography sx={{ fontSize: '0.82rem', color: c.g100, fontWeight: 500 }}>
                    You'll be notified when complete
                  </Typography>
                </Box>
              ) : Notification.permission !== 'denied' ? (
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  startIcon={<NotifyIcon sx={{ fontSize: 15 }} />}
                  onClick={handleRequestNotify}
                  sx={{
                    borderRadius: '8px', fontWeight: 500, fontSize: '0.82rem',
                    textTransform: 'none', color: c.g200,
                    '&:hover': { backgroundColor: c.g50, color: c.g300 },
                  }}
                >
                  Notify me when done
                </Button>
              ) : (
                <Typography sx={{ fontSize: '0.78rem', color: c.g100, textAlign: 'center' }}>
                  Processing in background…
                </Typography>
              ))}
            </Box>}
          </Collapse>
        </Box>
      </Slide>
    );
  };

  return (
    <>
      {/* Main modal — only for select step */}
      <ModalComponent open={open} onClose={handleCloseModal} style={{ maxWidth: 520, width: 'calc(100% - 1rem)', mx: 'auto' }}>
        <Paper sx={{
          background: c.whiteBG,
          borderRadius: { xs: '12px', sm: '16px' },
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          mx: { xs: '0.5rem', sm: 0 },
        }}>
          <PerfectScrollbar style={{ maxHeight: '90vh' }}>
            {renderSelect()}
          </PerfectScrollbar>
        </Paper>
      </ModalComponent>

      {/* Snackbar — visible during processing and complete */}
      {(step === 'processing' || step === 'complete') && renderSnackbar()}
    </>
  );
};

export default PitchDeckImportPopup;
